import React, { useState } from "react";
import { Button, Modal, Input, Upload, notification, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useAtom } from "jotai";
import { accessTokenAtom } from "../atoms";
import { useMsal } from "@azure/msal-react";

const BulkUploadModal = ({ label, uploadType, templateUrl = "/template.csv" }) => {
  const [accessToken] = useAtom(accessTokenAtom);
  const { accounts } = useMsal();

  // Logged-in user (from MSAL)
  const userName = accounts?.[0]?.name || accounts?.[0]?.username || "Unknown User";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [api, contextHolder] = notification.useNotification();

  const expectedHeaders = [
    "TypeOfGreenInstrument",
    "Category",
    "Vintage",
    "ProjectName",
    "ProjectLocation",
    "ProjectSerialNo",
    "ProjectLink",
    "Cost",
    "AvailableQuantity",
    "TotalQuantity",
  ];

  const currentDate = new Date().toLocaleDateString("en-GB");

  const notify = (type, title, msg) => {
    api[type]({
      message: title,
      description: msg,
      placement: "top",
      duration: 4,
    });
  };

  const beforeUpload = async (newFile) => {
    if (file) {
      notify("error", "Already Uploaded", "Remove the existing file before uploading another.");
      return Upload.LIST_IGNORE;
    }

    if (!newFile.name.toLowerCase().endsWith(".csv")) {
      notify("error", "Invalid File", "Only CSV files are allowed.");
      return Upload.LIST_IGNORE;
    }

    try {
      const text = await newFile.text();
      const firstLine = text.split(/\r?\n/)[0] || "";
      const headers = firstLine.split(",").map((h) => h.trim());

      const missing = expectedHeaders.filter((c) => !headers.includes(c));
      const extra = headers.filter((c) => !expectedHeaders.includes(c));

      if (missing.length > 0) {
        notify("error", "Invalid Template", `Missing columns: ${missing.join(", ")}`);
        return Upload.LIST_IGNORE;
      }

      if (extra.length > 0) {
        notify("error", "Invalid Template", `Unexpected columns: ${extra.join(", ")}`);
        return Upload.LIST_IGNORE;
      }

      // Validate TypeOfGreenInstrument based on uploadType
      const rows = text.split(/\r?\n/).slice(1);
      const typeIndex = headers.indexOf("TypeOfGreenInstrument");

      if (typeIndex === -1) {
        notify("error", "Invalid Template", "Missing TypeOfGreenInstrument column.");
        return Upload.LIST_IGNORE;
      }

      for (let row of rows) {
        if (!row.trim()) continue;

        const cols = row.split(",").map((v) => v.trim());
        if (cols[typeIndex] !== uploadType) {
          notify(
            "error",
            "Invalid Data",
            `This upload requires TypeOfGreenInstrument = ${uploadType} for all rows.`
          );
          return Upload.LIST_IGNORE;
        }
      }

      setFile(newFile);
      setFileList([newFile]);
    } catch {
      notify("error", "File Error", "Unable to read file.");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleUpload = async () => {
    if (!file) {
      notify("error", "No File", "Please select a file before submitting.");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadedBy", userName); // MSAL username
    formData.append("uploadType", uploadType);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}ActiveProject/BulkUploadActiveProject`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        notify("success", "Upload Successful", result.message || "File uploaded successfully.");
        setIsModalOpen(false);
        setFile(null);
        setFileList([]);
      } else {
        notify("error", "Upload Failed", result.message || "Server error occurred.");
      }
    } catch (err) {
      notify("error", "Network Error", err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {contextHolder}

      <Button
        type="primary"
        style={{ marginLeft: 10 }}
        onClick={() => {
          setFile(null);
          setFileList([]);
          setIsModalOpen(true);
        }}
      >
        Bulk Upload
      </Button>

      <Modal
        title={`Bulk Upload ${label}`}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setFile(null);
          setFileList([]);
        }}
        width="45%"
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalOpen(false);
              setFile(null);
              setFileList([]);
            }}
          >
            Return
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploading}
            disabled={!file}
            onClick={handleUpload}
          >
            Submit
          </Button>,
        ]}
      >
        {uploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 999,
            }}
          >
            <Spin size="large" />
          </div>
        )}

        <div className="py-3">
          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">Date Created</label>
            <div className="col-sm-8">
              <Input disabled value={currentDate} className="w-50" />
            </div>
          </div>

          <div className="mb-3 row">
            <label className="col-sm-4 col-form-label">Upload File</label>
            <div className="col-sm-8">
              <Upload
                accept=".csv"
                beforeUpload={beforeUpload}
                fileList={fileList}
                maxCount={1}
                onRemove={() => {
                  setFile(null);
                  setFileList([]);
                }}
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>

              {!file && (
                <small style={{ color: "red" }}>
                  Only upload csv file. Click{" "}
                  <a href={templateUrl} download>
                    here
                  </a>{" "}
                  for template.
                </small>
              )}

              {file && (
                <p style={{ marginTop: 8 }}>
                  Selected File: <b>{file.name}</b>
                </p>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BulkUploadModal;
